import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { portalApi, portalError } from "./portalApi";
import { Btn, Input, Label, Select, Modal, Badge, Empty } from "../components/UI";
import { Plus, CalendarClock, CalendarPlus, MessageCircleWarning, CalendarSync } from "lucide-react";
import { toast } from "sonner";

const REQ_EMPTY = { type: "Individual Therapy", preferred_date: "", preferred_time: "", reason: "" };

export default function PortalAppointments() {
  const [appts, setAppts] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [open, setOpen] = useState(false);
  const [resched, setResched] = useState(null);
  const [missed, setMissed] = useState(null);
  const [form, setForm] = useState(REQ_EMPTY);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    portalApi.get("/portal/appointments").then((r) => setAppts(r.data));
    portalApi.get("/portal/appointment-requests").then((r) => setReqs(r.data));
  };
  useEffect(() => { load(); }, []);

  const submitNew = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post("/portal/appointment-requests", form);
      toast.success("Request sent - the clinic will confirm your appointment");
      setOpen(false); setForm(REQ_EMPTY); load();
    } catch (err) { toast.error(portalError(err)); }
    setBusy(false);
  };

  const submitResched = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post(`/portal/appointments/${resched.id}/reschedule-request`,
        { preferred_date: form.preferred_date, preferred_time: form.preferred_time, reason: form.reason });
      toast.success("Reschedule request sent - the clinic will confirm the new time");
      setResched(null); setForm(REQ_EMPTY); load();
    } catch (err) { toast.error(portalError(err)); }
    setBusy(false);
  };

  const submitMissedReason = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.post(`/portal/appointments/${missed.id}/missed-reason`, { reason });
      toast.success("Thanks - your care team has been notified");
      setMissed(null); setReason(""); load();
    } catch (err) { toast.error(portalError(err)); }
    setBusy(false);
  };

  const downloadIcs = async (a) => {
    try {
      const r = await portalApi.get(`/portal/appointments/${a.id}/ics`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const el = document.createElement("a");
      el.href = url; el.download = `appointment-${a.date}.ics`; el.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(portalError(err)); }
  };

  const today = dayjs().format("YYYY-MM-DD");
  const pendingByAppt = new Set(reqs.filter((r) => r.status === "pending" && r.appointment_id).map((r) => r.appointment_id));
  const upcoming = appts.filter((a) => a.date >= today && !["cancelled", "rescheduled", "missed", "no-show", "completed"].includes(a.status));
  const past = appts.filter((a) => !upcoming.includes(a));

  const Row = (a) => {
    const canReschedule = ["scheduled", "checked_in"].includes(a.status) && a.date >= today;
    const isMissed = ["missed", "no-show"].includes(a.status);
    return (
      <div key={a.id} className="rounded-xl bg-white border border-[#EFEBE0] px-4 py-3" data-testid={`portal-appt-${a.id}`}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-28">
            <div className="font-bold text-[#3E2723] text-sm">{dayjs(a.date).format("MMM D, YYYY")}</div>
            <div className="text-xs text-[#7B6157]">{a.start_time}{a.end_time ? ` to ${a.end_time}` : ""}</div>
          </div>
          <div className="text-sm text-[#5D4037]">{a.type}</div>
          <span className="ml-auto"><Badge value={a.status || "scheduled"} /></span>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {canReschedule && (pendingByAppt.has(a.id) ? (
            <span className="rounded-full bg-[#FFF8E1] px-2.5 py-1 text-[10px] font-bold text-[#8D6E00]">Reschedule request pending</span>
          ) : (
            <button data-testid={`portal-resched-btn-${a.id}`} onClick={() => { setForm({ ...REQ_EMPTY, type: a.type }); setResched(a); }}
              className="flex items-center gap-1 rounded-full bg-[#F5F2EB] px-2.5 py-1 text-[11px] font-bold text-[#5D4037] hover:bg-[#EDE7DC]"><CalendarSync size={11} /> Request Reschedule</button>
          ))}
          {canReschedule && (
            <button data-testid={`portal-ics-btn-${a.id}`} onClick={() => downloadIcs(a)}
              className="flex items-center gap-1 rounded-full bg-[#E3F2FD] px-2.5 py-1 text-[11px] font-bold text-[#1565C0] hover:bg-[#BBDEFB]"><CalendarPlus size={11} /> Add to Calendar</button>
          )}
          {isMissed && (a.missed_reason ? (
            <span className="text-[11px] text-[#84675B]">Your note: "{a.missed_reason}"</span>
          ) : (
            <button data-testid={`portal-missed-reason-btn-${a.id}`} onClick={() => { setReason(""); setMissed(a); }}
              className="flex items-center gap-1 rounded-full bg-[#FFEBEE] px-2.5 py-1 text-[11px] font-bold text-[#C62828] hover:bg-[#FFCDD2]"><MessageCircleWarning size={11} /> Tell us what happened</button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="portal-appointments-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold text-[#3E2723]">Appointments</h1>
          <p className="text-sm text-[#7B6157]">View your schedule, request visits, reschedule & add to your calendar.</p>
        </div>
        <Btn data-testid="portal-request-appt-btn" onClick={() => { setForm(REQ_EMPTY); setOpen(true); }}><Plus size={15} /> Request Appointment</Btn>
      </div>

      {reqs.filter((r) => r.status === "pending").length > 0 && (
        <div className="rounded-2xl border border-[#FFB300] bg-[#FFF8E1] p-4" data-testid="portal-pending-requests">
          <div className="flex items-center gap-2 text-sm font-bold text-[#8D6E00]"><CalendarClock size={15} /> Pending requests</div>
          {reqs.filter((r) => r.status === "pending").map((r) => (
            <div key={r.id} className="mt-2 text-sm text-[#5D4037]">
              {r.kind === "reschedule" ? "Reschedule" : r.type} - preferred {dayjs(r.preferred_date).format("MMM D")} {r.preferred_time} · awaiting clinic confirmation
            </div>
          ))}
        </div>
      )}

      <div>
        <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-2">Upcoming</h3>
        {upcoming.length === 0 ? <Empty text="No upcoming appointments" /> : <div className="space-y-2">{upcoming.map(Row)}</div>}
      </div>
      <div>
        <h3 className="font-heading text-sm font-bold text-[#3E2723] mb-2">History</h3>
        {past.length === 0 ? <p className="text-sm text-[#84675B]">No past appointments.</p> : <div className="space-y-2">{past.slice(0, 15).map(Row)}</div>}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Request an Appointment">
        <form onSubmit={submitNew} className="space-y-4">
          <div><Label>Visit type</Label>
            <Select data-testid="portal-req-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {["Individual Therapy", "Medication Review", "Telehealth Session", "Intake Assessment"].map((t) => <option key={t}>{t}</option>)}
            </Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Preferred date</Label><Input data-testid="portal-req-date" type="date" min={dayjs().format("YYYY-MM-DD")} value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} required /></div>
            <div><Label>Preferred time</Label><Input data-testid="portal-req-time" type="time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} /></div>
          </div>
          <div><Label>Reason (optional)</Label><Input data-testid="portal-req-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Anything the clinic should know" /></div>
          <p className="text-xs text-[#84675B]">The clinic reviews every request and will confirm or offer another time.</p>
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn data-testid="portal-req-submit" type="submit" disabled={busy}>{busy ? "Sending…" : "Send Request"}</Btn>
          </div>
        </form>
      </Modal>

      <Modal open={!!resched} onClose={() => setResched(null)} title={resched ? `Reschedule: ${resched.type}` : ""}>
        {resched && (
          <form onSubmit={submitResched} className="space-y-4">
            <div className="rounded-xl bg-[#F5F2EB] p-3 text-sm text-[#5D4037]">Currently {dayjs(resched.date).format("dddd, MMM D")} at {resched.start_time}</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>New preferred date</Label><Input data-testid="portal-resched-date" type="date" min={dayjs().format("YYYY-MM-DD")} value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} required /></div>
              <div><Label>New preferred time</Label><Input data-testid="portal-resched-time" type="time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} /></div>
            </div>
            <div><Label>Reason (optional)</Label><Input data-testid="portal-resched-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="e.g., work conflict" /></div>
            <div className="flex justify-end gap-2">
              <Btn type="button" variant="ghost" onClick={() => setResched(null)}>Cancel</Btn>
              <Btn data-testid="portal-resched-submit" type="submit" disabled={busy}>{busy ? "Sending…" : "Request Reschedule"}</Btn>
            </div>
          </form>
        )}
      </Modal>

      <Modal open={!!missed} onClose={() => setMissed(null)} title="Tell us what happened">
        {missed && (
          <form onSubmit={submitMissedReason} className="space-y-4">
            <div className="rounded-xl bg-[#FFEBEE] p-3 text-sm text-[#C62828]">Missed: {missed.type} on {dayjs(missed.date).format("MMM D")} at {missed.start_time}</div>
            <div><Label>What happened?</Label>
              <textarea data-testid="portal-missed-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} required rows={3}
                className="w-full rounded-xl border border-[#E5DFD3] bg-white px-3 py-2 text-sm text-[#3E2723] focus:outline-none focus:ring-2 focus:ring-[#8D6E63]"
                placeholder="e.g., transportation fell through, family emergency…" /></div>
            <p className="text-xs text-[#84675B]">Your care team will see this and may reach out to reschedule.</p>
            <div className="flex justify-end gap-2">
              <Btn type="button" variant="ghost" onClick={() => setMissed(null)}>Cancel</Btn>
              <Btn data-testid="portal-missed-reason-submit" type="submit" disabled={busy}>{busy ? "Sending…" : "Send to Care Team"}</Btn>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
